import type { InitialCarePlan } from '@seen/core/models/care-plans/InitialCarePlan'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { Button } from '@seen/ui'
import { IconX } from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import type { JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { ActivityComment } from '#src/components/activity/ActivityComment.tsx'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { CommentComposer } from '#src/components/CommentComposer.tsx'
import {
  DrawerContentDeprecated,
  DrawerDeprecated,
} from '#src/components/core/Drawer.tsx'
import { useSearch } from '#src/hooks/useSearch.ts'

export function CarePlanActivitiesDrawer({
  carePlan,
}: {
  carePlan: InitialCarePlan
}): JSX.Element {
  const navigate = useNavigate()
  const params = useParams()
  const isNew = params['*'] === 'activities'

  const { data: comments = [] } = useSearch(
    CommentCommunication,
    CommentCommunication.searchQuery([carePlan.reference])
  )

  const sortedComments = orderBy(comments, (c) => c.sent, ['asc'])

  const handleClose = (): void => {
    void navigate('..', { replace: true })
  }

  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen) {
      handleClose()
    }
  }

  return (
    <DrawerDeprecated
      direction="right"
      open={isNew}
      onOpenChange={handleOpenChange}
    >
      <DrawerContentDeprecated className="top-0 right-0 left-auto mt-0 h-screen w-[85%] max-w-xl p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Care Plan Activities</h2>
          <div className="ml-auto flex gap-2">
            <Button
              variant="ghost"
              size="icon"
              aria-label="Close activities drawer"
              onClick={handleClose}
              className="p-0 text-muted-foreground"
            >
              <IconX size={18} />
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="-mx-1.5">
            <CommentComposer
              partOf={[carePlan.reference]}
              subject={carePlan.patientSubject}
            />
          </div>
          {sortedComments.length > 0 && (
            <ActivityList>
              {sortedComments.map((comment) => (
                <ActivityComment
                  key={comment.reference.reference}
                  comment={comment}
                />
              ))}
            </ActivityList>
          )}
        </div>
      </DrawerContentDeprecated>
    </DrawerDeprecated>
  )
}
